/**
 * REST client helpers for Work Area and artifact applicability endpoints.
 */
import { buildBackendUrl } from '@/lib/auth-routing';
import type {
  AddWorkAreaCodebaseBody,
  ArtifactApplicability,
  ArtifactApplicabilityListResponse,
  CreateArtifactApplicabilityBody,
  CreateWorkAreaBody,
  PatchWorkAreaBody,
  SetWorkAreaEnvVarBody,
  SuccessResponse,
  WorkArea,
  WorkAreaCodebase,
  WorkAreaCodebaseListResponse,
  WorkAreaEnvVarListResponse,
  WorkAreaListResponse,
  WorkAreaResponse,
} from './api';

async function readApiError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `API error ${String(res.status)}`;
  } catch {
    return `API error ${String(res.status)}`;
  }
}

async function fetchOrThrow(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(buildBackendUrl(url), {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
  if (!res.ok) {
    const err = new Error(await readApiError(res)) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res;
}

function jsonRequest(body: unknown): RequestInit {
  return {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export async function listWorkAreas(): Promise<WorkAreaListResponse> {
  const res = await fetchOrThrow('/api/work-areas');
  return (await res.json()) as WorkAreaListResponse;
}

export async function createWorkArea(body: CreateWorkAreaBody): Promise<WorkArea> {
  const res = await fetchOrThrow('/api/work-areas', {
    method: 'POST',
    ...jsonRequest(body),
  });
  const data = (await res.json()) as WorkAreaResponse;
  return data.workArea;
}

export async function getWorkArea(workAreaId: string): Promise<WorkArea> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}`);
  const data = (await res.json()) as WorkAreaResponse;
  return data.workArea;
}

export async function patchWorkArea(
  workAreaId: string,
  body: PatchWorkAreaBody
): Promise<WorkArea> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}`, {
    method: 'PATCH',
    ...jsonRequest(body),
  });
  const data = (await res.json()) as WorkAreaResponse;
  return data.workArea;
}

export async function deleteWorkArea(workAreaId: string): Promise<SuccessResponse> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}`, {
    method: 'DELETE',
  });
  return (await res.json()) as SuccessResponse;
}

export async function listWorkAreaCodebases(
  workAreaId: string
): Promise<WorkAreaCodebaseListResponse> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}/codebases`);
  return (await res.json()) as WorkAreaCodebaseListResponse;
}

export async function addWorkAreaCodebase(
  workAreaId: string,
  body: AddWorkAreaCodebaseBody
): Promise<WorkAreaCodebase> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}/codebases`, {
    method: 'POST',
    ...jsonRequest(body),
  });
  return (await res.json()) as WorkAreaCodebase;
}

export async function removeWorkAreaCodebase(
  workAreaId: string,
  codebaseId: string
): Promise<SuccessResponse> {
  const res = await fetchOrThrow(
    `/api/work-areas/${encodeURIComponent(workAreaId)}/codebases/${encodeURIComponent(codebaseId)}`,
    { method: 'DELETE' }
  );
  return (await res.json()) as SuccessResponse;
}

export async function listWorkAreaEnvVars(workAreaId: string): Promise<WorkAreaEnvVarListResponse> {
  const res = await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}/env`);
  return (await res.json()) as WorkAreaEnvVarListResponse;
}

export async function setWorkAreaEnvVar(
  workAreaId: string,
  input: SetWorkAreaEnvVarBody
): Promise<void> {
  await fetchOrThrow(`/api/work-areas/${encodeURIComponent(workAreaId)}/env`, {
    method: 'PUT',
    ...jsonRequest(input),
  });
}

export async function deleteWorkAreaEnvVar(
  workAreaId: string,
  key: string,
  codebaseId?: string
): Promise<void> {
  const query = codebaseId ? `?${new URLSearchParams({ codebaseId }).toString()}` : '';
  await fetchOrThrow(
    `/api/work-areas/${encodeURIComponent(workAreaId)}/env/${encodeURIComponent(key)}${query}`,
    { method: 'DELETE' }
  );
}

export async function listApplicability(
  artifactId: string
): Promise<ArtifactApplicabilityListResponse> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/applicability`);
  return (await res.json()) as ArtifactApplicabilityListResponse;
}

export async function createApplicability(
  artifactId: string,
  body: CreateArtifactApplicabilityBody
): Promise<ArtifactApplicability> {
  const res = await fetchOrThrow(`/api/artifacts/${encodeURIComponent(artifactId)}/applicability`, {
    method: 'POST',
    ...jsonRequest(body),
  });
  const data = (await res.json()) as { applicability: ArtifactApplicability };
  return data.applicability;
}

export async function deleteApplicability(
  artifactId: string,
  applicabilityId: string
): Promise<SuccessResponse> {
  const res = await fetchOrThrow(
    `/api/artifacts/${encodeURIComponent(artifactId)}/applicability/${encodeURIComponent(applicabilityId)}`,
    { method: 'DELETE' }
  );
  return (await res.json()) as SuccessResponse;
}
